import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Lock, LogOut, Users, Filter, Megaphone, Video, Image as ImageIcon,
  TrendingUp, DollarSign, Loader2, Package, CreditCard, Landmark, Check,
} from "lucide-react";
import { api, apiError } from "../lib/api";
import AdminPins from "../components/AdminPins";
import AdminRenalifeMaterials from "../components/AdminRenalifeMaterials";
import AdminMultinivel from "../components/AdminMultinivel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-white/10 bg-[#121212] p-5">
    <Icon className="text-purple-400" size={22} />
    <div className="font-display font-black text-2xl mt-3">{value}</div>
    <div className="text-xs text-zinc-500 mt-1">{label}</div>
  </div>
);

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking, false=login, true=admin
  const [form, setForm] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);

  const checkAdmin = async () => {
    try {
      const { data: me } = await api.get("/auth/me");
      if (me.role === "admin") { setAuthed(true); return true; }
    } catch (err) { console.debug("admin auth check failed:", err?.response?.status); }
    setAuthed(false);
    return false;
  };

  useEffect(() => { checkAdmin(); }, []);

  useEffect(() => {
    if (authed === true) {
      api.get("/admin/overview").then((r) => setData(r.data)).catch((err) => console.error("Error cargando overview:", err));
      loadPayments();
    }
  }, [authed]);

  const loadPayments = () => api.get("/admin/payments").then((r) => setPayments(r.data)).catch((err) => console.error("Error cargando pagos:", err));

  const approve = async (payId) => {
    try {
      await api.post(`/admin/payments/${payId}/approve`);
      toast.success("Pago aprobado y plan activado");
      loadPayments();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/login", form);
      setAuthed(true);
      toast.success("Bienvenido, Administrador");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.debug("logout request failed:", err); }
    setAuthed(false);
    setData(null);
  };

  if (authed === null)
    return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-purple-500" size={32} /></div>;

  if (authed === false)
    return (
      <div className="min-h-screen bg-[#050505] grid-texture flex items-center justify-center px-5">
        <div className="w-full max-w-sm">
          <Link to="/" className="block text-center text-xl font-display font-black mb-6">MULTI<span className="text-purple-500">CONTACTOS</span></Link>
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-8 glow-purple">
            <div className="flex items-center gap-2 mb-1"><ShieldCheck className="text-yellow-400" size={20} /><h1 className="font-display font-bold text-2xl">Panel de Administrador</h1></div>
            <p className="text-sm text-zinc-500 mb-6">Acceso restringido.</p>
            <form onSubmit={login} className="space-y-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Usuario / Celular</Label>
                <Input data-testid="admin-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="3342802102" className="mt-1.5 bg-black border-white/15 rounded-xl py-6 focus-visible:ring-purple-500" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Contraseña</Label>
                <Input data-testid="admin-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••" className="mt-1.5 bg-black border-white/15 rounded-xl py-6 focus-visible:ring-purple-500" />
              </div>
              <Button data-testid="admin-login-button" type="submit" disabled={loading} className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl py-6">
                {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Lock size={16} className="mr-2" />} Ingresar
              </Button>
            </form>
          </div>
        </div>
      </div>
    );

  const p = data?.platform || {};
  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505] text-white">
      <nav className="border-b border-white/10 glass sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-black"><ShieldCheck size={20} className="text-yellow-400" /> Admin · MULTI<span className="text-purple-500">CONTACTOS</span></div>
          <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-300 hover:text-white border border-white/15 rounded-lg px-3 py-1.5 hover:bg-white/5 transition-colors"><LogOut size={15} /> Cerrar sesión</button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-display font-black tracking-tight text-3xl mb-6">Resumen de la plataforma</h1>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <Stat icon={Users} label="Usuarios" value={p.users ?? "-"} />
          <Stat icon={Filter} label="Embudos" value={p.funnels ?? "-"} />
          <Stat icon={Megaphone} label="Campañas" value={p.campaigns ?? "-"} />
          <Stat icon={TrendingUp} label="Prospectos calificados" value={p.qualified_leads ?? "-"} />
          <Stat icon={Users} label="Prospectos totales" value={p.leads ?? "-"} />
          <Stat icon={ImageIcon} label="Imágenes generadas" value={p.images_generated ?? "-"} />
          <Stat icon={Video} label="Videos generados" value={p.videos_generated ?? "-"} />
        </div>

        {/* Costs */}
        <div className="rounded-2xl border border-yellow-400/30 bg-[#16130a] p-6 mb-10">
          <div className="flex items-center gap-2 mb-4"><DollarSign className="text-yellow-400" size={22} /><h2 className="font-display font-bold text-xl">Costos internos (privado)</h2></div>
          <p className="text-sm text-zinc-400 mb-4">Costo aproximado por unidad de generación con IA. Esta información NO es visible para los usuarios.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {data?.costs?.unit_costs_usd && Object.entries(data.costs.unit_costs_usd).map(([k, v]) => (
              <div key={k} className="rounded-xl bg-black border border-white/10 p-4 text-center">
                <div className="text-xs uppercase tracking-wider text-zinc-500">{k === "text" ? "Texto" : k === "image" ? "Imagen" : k === "video" ? "Video" : "Lead"}</div>
                <div className="font-display font-black text-2xl text-yellow-impact mt-1">${v}</div>
                <div className="text-[10px] text-zinc-600">USD c/u</div>
              </div>
            ))}
          </div>
          <p className="text-sm text-zinc-400 mt-4">Multiplicador de precio: <span className="text-yellow-impact font-bold">x{data?.costs?.price_multiplier}</span> (precio = costo × {data?.costs?.price_multiplier}).</p>
        </div>

        {/* Packages cost breakdown */}
        <div className="flex items-center gap-2 mb-4"><Package className="text-purple-400" size={22} /><h2 className="font-display font-bold text-xl">Paquetes: costo, precio y margen</h2></div>
        <div className="grid sm:grid-cols-3 gap-5">
          {data?.packages?.map((pk) => (
            <div key={pk.id} data-testid="admin-package" className="rounded-2xl border border-white/10 bg-[#121212] p-6">
              <h3 className="font-display font-bold text-lg">{pk.name}</h3>
              <div className="mt-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-zinc-500">Costo mensual</span><span className="text-zinc-300">${pk.cost} USD</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Precio (x{data?.costs?.price_multiplier})</span><span className="text-yellow-impact font-bold">${pk.price} USD</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Margen</span><span className="text-green-400 font-semibold">${pk.margin} USD</span></div>
              </div>
              <div className="mt-3 pt-3 border-t border-white/10 text-xs text-zinc-500 space-y-0.5">
                {Object.entries(pk.quota).map(([k, v]) => <div key={k} className="flex justify-between"><span>{k === "text" ? "Textos" : k === "image" ? "Imágenes" : k === "video" ? "Videos" : k === "pin" ? "PINs" : "Leads"}</span><span>{v}</span></div>)}
              </div>
            </div>
          ))}
        </div>

        {/* Payments */}
        <div className="flex items-center gap-2 mb-4 mt-12"><CreditCard className="text-purple-400" size={22} /><h2 className="font-display font-bold text-xl">Pagos y suscripciones</h2></div>
        <div className="rounded-2xl border border-white/10 bg-[#121212] overflow-hidden">
          {payments.length === 0 ? (
            <p className="text-zinc-500 text-sm p-6">Aún no hay pagos registrados.</p>
          ) : (
            <div className="divide-y divide-white/5">
              {payments.map((tx) => (
                <div key={tx.session_id || tx.pay_id} data-testid="admin-payment-row" className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{tx.plan_name || tx.plan_id}</span>
                      <span className="text-yellow-impact font-bold">${Math.round(tx.amount)} USD</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${tx.payment_status === "paid" ? "border-green-500/40 text-green-400" : tx.payment_status === "pending_review" ? "border-yellow-400/40 text-yellow-impact" : "border-zinc-600 text-zinc-400"}`}>
                        {tx.payment_status === "paid" ? "Pagado" : tx.payment_status === "pending_review" ? "Por revisar" : tx.payment_status}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
                        {tx.method === "bank_transfer" ? <><Landmark size={12} /> Transferencia</> : <><CreditCard size={12} /> Tarjeta</>}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                      {tx.user_name || tx.user_id} {tx.user_phone ? `· ${tx.user_phone}` : ""} {tx.reference ? `· Ref: ${tx.reference}` : ""}
                    </div>
                  </div>
                  {tx.method === "bank_transfer" && !tx.processed && (
                    <button data-testid="approve-payment" onClick={() => approve(tx.pay_id)} className="shrink-0 inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-500 text-white text-sm font-semibold rounded-xl px-4 py-2">
                      <Check size={15} /> Aprobar y activar
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* PINs de acceso (tradicionales y RENALIFE) */}
        <AdminPins />

        {/* Materiales RENALIFE prediseñados */}
        <AdminRenalifeMaterials />

        {/* Campaña MULTINIVEL */}
        <AdminMultinivel />
      </main>
    </div>
  );
}
